import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchWorkersByProject, fetchMembersByProject } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { useI18n } from "../../i18n";

export default function BuildProjectMemberListPage() {
  const { businessDomainId, businessUnitId, projectId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const { locale } = useI18n();
  const t = (id) => intl.formatMessage({ id });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const basePath = `/build/${businessDomainId}/business-units/${businessUnitId}/projects/${projectId}`;

  const getMemberTypeLabel = (industryId) =>
    industryId === "9999" ? t("member.type.company") : t("member.type.market");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [workers, members] = await Promise.all([
        fetchWorkersByProject(projectId).catch(() => []),
        fetchMembersByProject(projectId).catch(() => []),
      ]);
      // memberId をキーにした Map を作成して Worker に industryId を合成
      const memberMap = {};
      (Array.isArray(members) ? members : []).forEach((m) => {
        memberMap[m.memberId] = m;
      });
      setRows(
        (Array.isArray(workers) ? workers : []).map((w) => {
          const m = memberMap[w.memberId];
          const firstName = m?.firstName || "";
          const lastName  = m?.lastName  || "";
          const fullName  = locale === "ja"
            ? `${lastName} ${firstName}`.trim()
            : `${firstName} ${lastName}`.trim();
          return {
            ...w,
            memberName: fullName || w.memberName,
            industryId: m?.industryId,
            memberTypeLabel: getMemberTypeLabel(m?.industryId),
          };
        })
      );
    } catch (e) { notify.apiError(e); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => { loadData(); }, [loadData]);

  const columns = [
    { field: "memberName",     headerName: t("member.name"),              flex: 1, minWidth: 160 },
    { field: "memberTypeLabel",headerName: t("member.type"),              width: 80 },
    { field: "departmentName", headerName: t("department.name"),          flex: 1, minWidth: 160 },
    { field: "workerName",     headerName: t("project.member.worker.name"), flex: 1, minWidth: 160 },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("project.member.list.title")}</Typography>
        <Button variant="contained" onClick={() => navigate(`${basePath}/members/assign`)}>
          {t("project.member.assign.title")}
        </Button>
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(r) => r.workerId ?? r.memberId}
        loading={loading}
        autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        onRowClick={(params) => navigate(`${basePath}/members/${params.row.memberId}`)}
        sx={{
          "& .MuiDataGrid-row": { cursor: "pointer" },
          "& .MuiDataGrid-row:hover": { backgroundColor: "action.hover" },
        }}
      />
      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={() => navigate(`/build/${businessDomainId}/business-units/${businessUnitId}/projects/${projectId}`)}>{t("common.back")}</Button>
      </Box>
    </Box>
  );
}
